import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { RefreshCw, Plug, Trash2, ChevronDown, ChevronRight, ShieldCheck, Loader2, QrCode, Upload, FileText, Layers, Coins, CheckCircle2, Pencil, Sparkles } from "lucide-react";
import { toast } from "sonner";
import QRScanDialog from "@/components/QRScanDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import BillingPanel from "@/pages/BillingPanel";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: "konten", label: "Konten" },
  { key: "abo", label: "Abo & Plan" },
  { key: "broker", label: "Broker" },
  { key: "darstellung", label: "Darstellung" },
  { key: "datenschutz", label: "Datenschutz" },
];

export default function Settings() {
  const [tab, setTab] = useState("konten");
  const { user, prefs, updatePrefs } = useAuth();
  const [themeBusy, setThemeBusy] = useState(false);
  const [compactBusy, setCompactBusy] = useState(false);
  const dark = (prefs?.theme || "dark") === "dark";
  const compact = !!prefs?.compact;

  const onToggleDark = async (val) => {
    setThemeBusy(true);
    try {
      await updatePrefs({ theme: val ? "dark" : "light" });
      toast.success(val ? "Dunkles Erscheinungsbild aktiv" : "Helles Erscheinungsbild aktiv", {
        description: "Wird auf allen Geräten übernommen.",
      });
    } catch (e) {
      toast.error("Konnte nicht gespeichert werden", { description: e?.response?.data?.detail || "Bitte erneut versuchen." });
    } finally { setThemeBusy(false); }
  };
  const onToggleCompact = async (val) => {
    setCompactBusy(true);
    try { await updatePrefs({ compact: val }); }
    catch (e) { toast.error("Konnte nicht gespeichert werden", { description: e?.response?.data?.detail || "Bitte erneut versuchen." }); }
    finally { setCompactBusy(false); }
  };

  const displayName = (user && user !== false) ? (user.name || user.email?.split("@")[0] || "") : "";
  const email = (user && user !== false) ? (user.email || "") : "";

  return (
    <div>
      <PageHeader
        eyebrow="Einstellungen"
        title="Einstellungen"
        description="Konten, Broker, Darstellung und Datenschutz."
        testid="settings-header"
      />

      <Tabs value={tab} onValueChange={setTab} data-testid="settings-tabs">
        <TabsList className="rounded-full h-11 p-1 bg-secondary flex-wrap">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              className="rounded-full px-4 h-9 text-sm data-[state=active]:bg-background"
              data-testid={`settings-tab-${t.key}`}
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="konten" className="mt-6 space-y-4">
          <TourResetCard />
          <ProfileCard />
        </TabsContent>

        <TabsContent value="broker" className="mt-6 space-y-4">
          <OkxBrokerCard />
          <BitgetBrokerCard />
          <BinanceBrokerCard />
          <KrakenBrokerCard />
          <BitunixBrokerCard />
          <LeveXImportCard />
        </TabsContent>

        <TabsContent value="darstellung" className="mt-6">
          <div className="card-surface p-6 max-w-xl space-y-6">
            <ToggleRow
              label="Dunkles Erscheinungsbild"
              description="Standard-Theme. Deaktivieren für helle Ansicht. Wird pro Konto gespeichert."
              checked={dark}
              onChange={onToggleDark}
              disabled={themeBusy}
              testid="toggle-dark"
            />
            <Separator />
            <ToggleRow
              label="Kompaktere Ansicht"
              description="Weniger Abstand in Karten und Tabellen. Wird pro Konto gespeichert."
              checked={compact}
              onChange={onToggleCompact}
              disabled={compactBusy}
              testid="toggle-compact"
            />
          </div>
        </TabsContent>

        <TabsContent value="datenschutz" className="mt-6">
          <div className="card-surface p-6 max-w-2xl space-y-4">
            <h2 className="font-heading text-xl">Datenschutz</h2>
            <p className="text-sm text-muted-foreground">
              Trades und Journal-Einträge werden verschlüsselt gespeichert. Export und Löschung jederzeit möglich.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button variant="outline" className="rounded-full">Daten exportieren</Button>
              <Button variant="ghost" className="rounded-full text-[hsl(0_66%_72%)]">
                Konto löschen
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="abo" className="mt-6">
          <BillingPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TourResetCard() {
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/me/tour`, { status: "reset" }, { withCredentials: true });
      try {
        localStorage.removeItem("trademind:tour-step");
        localStorage.removeItem("trademind:tour-later-at");
      } catch {}
      toast.success("Product-Tour zurückgesetzt", { description: "Sie erscheint gleich unten rechts wieder." });
      setTimeout(() => window.location.assign("/app"), 800);
    } catch (e) {
      toast.error("Zurücksetzen fehlgeschlagen", { description: e?.response?.data?.detail || "" });
    } finally { setBusy(false); }
  };
  return (
    <div className="card-surface p-6 max-w-3xl" data-testid="tour-reset-card">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-full bg-secondary border border-border grid place-items-center shrink-0">
          <Sparkles className="h-5 w-5 text-primary" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-lg">Product-Tour</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Der Coach führt dich in ~60 Sekunden durch alle sieben Bereiche. Ideal, wenn du die Tour beim ersten Login übersprungen hast oder etwas nachschauen willst.
          </p>
          <div className="mt-4">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={reset}
              disabled={busy}
              data-testid="tour-reset-btn"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.5} />}
              Product-Tour nochmal ansehen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileCard() {
  const { user, prefs, updatePrefs } = useAuth();
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [timezone, setTimezone] = useState("Europe/Berlin");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user !== false) {
      setName(user.name || user.email?.split("@")[0] || "");
      setCurrency((prefs?.currency || "EUR").toUpperCase());
      setTimezone(prefs?.timezone || "Europe/Berlin");
    }
  }, [user, prefs?.currency, prefs?.timezone]);

  const displayEmail = (user && user !== false) ? (user.email || "") : "";
  const initialName = (user && user !== false) ? (user.name || user.email?.split("@")[0] || "") : "";
  const initialCurrency = (prefs?.currency || "EUR").toUpperCase();
  const initialTz = prefs?.timezone || "Europe/Berlin";
  const dirty = name.trim() !== initialName || currency.trim().toUpperCase() !== initialCurrency || timezone.trim() !== initialTz;

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await updatePrefs({
        name: name.trim(),
        currency: currency.trim().toUpperCase(),
        timezone: timezone.trim(),
      });
      toast.success("Profil gespeichert", { description: "Deine Änderungen sind übernommen." });
    } catch (e) {
      toast.error("Speichern fehlgeschlagen", { description: e?.response?.data?.detail || "Bitte erneut versuchen." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-surface p-6 max-w-2xl space-y-6" data-testid="settings-profile-card">
      <div>
        <h2 className="font-heading text-xl">Profil</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Deine Konto-Basisdaten. Änderungen werden für alle Geräte gespeichert.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Row label="Anzeigename">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Felix" data-testid="settings-name" maxLength={80} />
        </Row>
        <Row label="E-Mail">
          <Input value={displayEmail} disabled data-testid="settings-email" />
        </Row>
        <Row label="Basiswährung">
          <Input value={currency} onChange={(e) => setCurrency(e.target.value.slice(0, 8))} data-testid="settings-currency" placeholder="EUR" />
        </Row>
        <Row label="Zeitzone">
          <Input value={timezone} onChange={(e) => setTimezone(e.target.value.slice(0, 64))} data-testid="settings-timezone" placeholder="Europe/Berlin" />
        </Row>
      </div>
      <div className="pt-2">
        <Button className="rounded-full" onClick={save} disabled={saving || !dirty} data-testid="settings-profile-save">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} /> : null}
          {saving ? "Speichert…" : "Speichern"}
        </Button>
      </div>
    </div>
  );
}

function OkxBrokerCard() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [howto, setHowto] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [syncAllBusy, setSyncAllBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState({}); // { [label]: "sync" | "disconnect" }
  const [form, setForm] = useState({ api_key: "", api_secret: "", passphrase: "", demo: false, region: "global", label: "" });
  const [qrOpen, setQrOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/broker/okx/connections`, { withCredentials: true });
      setConnections(Array.isArray(res.data) ? res.data : []);
      if (!res.data || res.data.length === 0) setShowForm(true);
    } catch {
      setConnections([]);
      setShowForm(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({ api_key: "", api_secret: "", passphrase: "", demo: false, region: "global", label: "" });

  const connect = async () => {
    if (!form.api_key || !form.api_secret || !form.passphrase) {
      toast.error("Bitte alle drei Felder ausfüllen.");
      return;
    }
    const defaultLabel = connections.length === 0 ? "Haupt-Konto" : `Sub-Konto ${connections.length + 1}`;
    const label = (form.label || "").trim() || defaultLabel;
    // Duplikat-Check clientseitig
    if (connections.some((c) => (c.label || "Haupt-Konto") === label)) {
      toast.error("Bezeichnung bereits vergeben", { description: "Bitte wähle einen anderen Namen für dieses Sub-Konto." });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/api/broker/okx/connect`, { ...form, label }, { withCredentials: true });
      resetForm();
      setShowForm(false);
      await load();
      toast.success(`OKX „${label}" verbunden`, { description: "Verbindung getestet und verschlüsselt gespeichert." });
    } catch (e) {
      const raw = e?.response?.data?.detail || "Prüfe API-Key, Secret und Passphrase.";
      toast.error("Verbindung fehlgeschlagen", { description: raw.replace(/^Verbindung fehlgeschlagen:\s*/i, "") });
    } finally {
      setBusy(false);
    }
  };

  const disconnectOne = async (label) => {
    if (!window.confirm(`OKX-Konto „${label}" wirklich trennen? Zugehörige API-Trades werden entfernt (manuell verknüpfte bleiben erhalten).`)) return;
    setRowBusy((m) => ({ ...m, [label]: "disconnect" }));
    try {
      await axios.delete(`${API}/api/broker/okx/disconnect`, { params: { label }, withCredentials: true });
      toast.success(`„${label}" getrennt`);
      await load();
    } catch (e) {
      toast.error("Trennen fehlgeschlagen", { description: e?.response?.data?.detail || "" });
    } finally {
      setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; });
    }
  };

  const syncOne = async (label) => {
    setRowBusy((m) => ({ ...m, [label]: "sync" }));
    try {
      const res = await axios.post(`${API}/api/broker/okx/sync`, {}, { params: { label }, withCredentials: true });
      const d = res.data || {};
      const parts = [`${d.inserted || 0} Trades gepaart`, `${d.fetched || 0} Fills`];
      if (d.merged_partial_fills) parts.push(`${d.merged_partial_fills} Partial-Fills zusammengeführt`);
      toast.success(`Sync „${label}" abgeschlossen`, { description: parts.join(" · ") });
      await load();
    } catch (e) {
      toast.error("Sync fehlgeschlagen", { description: e?.response?.data?.detail || "OKX nicht erreichbar." });
    } finally {
      setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; });
    }
  };

  const renameOne = async (label) => {
    const raw = window.prompt(`Neue Bezeichnung für „${label}":`, label);
    if (raw === null) return;
    const nextLabel = raw.trim().slice(0, 40);
    if (!nextLabel || nextLabel === label) return;
    setRowBusy((m) => ({ ...m, [label]: "rename" }));
    try {
      await axios.patch(
        `${API}/api/broker/okx/connections/${encodeURIComponent(label)}`,
        { new_label: nextLabel },
        { withCredentials: true },
      );
      toast.success(`Umbenannt zu „${nextLabel}"`);
      await load();
    } catch (e) {
      toast.error("Umbenennen fehlgeschlagen", { description: e?.response?.data?.detail || "" });
    } finally {
      setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; });
    }
  };


  const syncAll = async () => {
    setSyncAllBusy(true);
    try {
      const res = await axios.post(`${API}/api/broker/okx/sync`, {}, { withCredentials: true });
      const d = res.data || {};
      if (d.multi) {
        toast.success(`Alle ${d.accounts_synced} Sub-Accounts synchronisiert`, {
          description: `${d.inserted || 0} Trades gepaart · ${d.fetched || 0} Fills gelesen`,
        });
      } else {
        toast.success(`Sync „${d.label || ""}" abgeschlossen`, {
          description: `${d.inserted || 0} Trades gepaart · ${d.fetched || 0} Fills`,
        });
      }
      await load();
    } catch (e) {
      toast.error("Sync fehlgeschlagen", { description: e?.response?.data?.detail || "OKX nicht erreichbar." });
    } finally {
      setSyncAllBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="card-surface p-10 text-center" data-testid="broker-loading">
        <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  const hasAny = connections.length > 0;
  const suggestedLabel = connections.length === 0 ? "Haupt-Konto" : `Sub-Konto ${connections.length + 1}`;

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="card-surface p-6" data-testid="broker-okx-card">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-full bg-secondary border border-border grid place-items-center">
              <Plug className="h-5 w-5 text-primary-foreground/80" strokeWidth={1.5} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-heading text-lg">OKX</h2>
                <Badge
                  variant="secondary"
                  className={`rounded-full text-xs ${
                    hasAny
                      ? "bg-[hsl(122_39%_49%/0.15)] text-[hsl(122_39%_65%)] border border-[hsl(122_39%_49%/0.35)]"
                      : "bg-secondary border border-border"
                  }`}
                >
                  {hasAny ? `${connections.length} ${connections.length === 1 ? "Konto verbunden" : "Konten verbunden"}` : "nicht verbunden"}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Krypto-Börse OKX. Read-only API — es werden keine Orders platziert. Du kannst mehrere Sub-Accounts hinzufügen.
              </p>
            </div>
          </div>
          {hasAny && connections.length > 1 && (
            <Button
              variant="outline"
              className="rounded-full"
              size="sm"
              onClick={syncAll}
              disabled={syncAllBusy}
              data-testid="broker-okx-sync-all"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${syncAllBusy ? "animate-spin" : ""}`} strokeWidth={1.5} />
              {syncAllBusy ? "Sync läuft…" : "Alle synchronisieren"}
            </Button>
          )}
        </div>

        {/* Connections-Liste */}
        {hasAny && (
          <div className="mt-5 space-y-3">
            {connections.map((c) => {
              const label = c.label || "Haupt-Konto";
              const isSync = rowBusy[label] === "sync";
              const isDisc = rowBusy[label] === "disconnect";
              return (
                <div
                  key={label}
                  className="rounded-xl border border-border bg-secondary/30 p-4 flex items-start justify-between gap-4 flex-wrap"
                  data-testid={`broker-okx-conn-${label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-full bg-background border border-border grid place-items-center shrink-0">
                      <Layers className="h-4 w-4 text-primary-foreground/70" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{label}</span>
                        {c.demo && (
                          <Badge variant="secondary" className="rounded-full text-[10px] bg-[hsl(32_60%_55%/0.15)] text-[hsl(32_60%_72%)] border border-[hsl(32_60%_55%/0.4)]">
                            Demo
                          </Badge>
                        )}
                        <Badge variant="secondary" className="rounded-full text-[10px] bg-secondary border border-border">
                          {c.region || "global"}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        Key {c.api_key_mask} · {c.last_sync_at ? `letzter Sync ${new Date(c.last_sync_at).toLocaleString("de-DE")}` : "noch nicht synchronisiert"}
                        {typeof c.last_sync_count === "number" && c.last_sync_count > 0 && ` · ${c.last_sync_count} Trades`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() => syncOne(label)}
                      disabled={isSync || isDisc || syncAllBusy}
                      data-testid={`broker-okx-sync-${label.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <RefreshCw className={`mr-2 h-4 w-4 ${isSync ? "animate-spin" : ""}`} strokeWidth={1.5} />
                      {isSync ? "Sync läuft…" : "Sync"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-full h-8 w-8"
                      onClick={() => renameOne(label)}
                      disabled={isSync || isDisc || rowBusy[label] === "rename"}
                      title="Umbenennen"
                      data-testid={`broker-okx-rename-${label.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[hsl(0_66%_72%)]"
                      onClick={() => disconnectOne(label)}
                      disabled={isSync || isDisc}
                      data-testid={`broker-okx-disconnect-${label.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <Trash2 className="mr-2 h-4 w-4" strokeWidth={1.5} /> Trennen
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* „Weiteres Konto hinzufügen" — nur wenn schon min. eins existiert und Form geschlossen */}
        {hasAny && !showForm && (
          <div className="mt-4">
            <Button
              variant="outline"
              className="rounded-full"
              size="sm"
              onClick={() => setShowForm(true)}
              data-testid="broker-okx-add-account"
            >
              <Coins className="mr-2 h-4 w-4" strokeWidth={1.5} /> Weiteres OKX-Konto hinzufügen
            </Button>
          </div>
        )}

        {/* Connect-Formular */}
        {showForm && (
          <div className="mt-6 space-y-4 border-t border-border pt-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-primary-foreground/80 hover:text-primary-foreground"
                onClick={() => setHowto((s) => !s)}
                data-testid="broker-okx-howto-toggle"
              >
                {howto ? <ChevronDown className="h-4 w-4" strokeWidth={1.5} /> : <ChevronRight className="h-4 w-4" strokeWidth={1.5} />}
                Wie erstelle ich einen Read-only-Key bei OKX?
              </button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={() => setQrOpen(true)}
                data-testid="broker-okx-scan-qr"
              >
                <QrCode className="mr-2 h-4 w-4" strokeWidth={1.5} /> QR-Code scannen
              </Button>
            </div>
            {howto && (
              <ol className="text-sm text-muted-foreground list-decimal pl-6 space-y-1" data-testid="broker-okx-howto">
                <li>Bei OKX einloggen → oben rechts auf dein Profilbild → <span className="text-foreground">„API"</span>.</li>
                <li>„Create V5 API Key" → Name z. B. „TradeMind Read-only".</li>
                <li>Bei <span className="text-foreground">Permissions</span> nur <span className="text-foreground">„Read"</span> ankreuzen — <span className="text-foreground">nicht</span> „Trade" oder „Withdraw".</li>
                <li>Eine Passphrase deiner Wahl setzen (frei wählbar, brauchst du gleich unten).</li>
                <li>API-Key, Secret Key und Passphrase kopieren und hier einfügen.</li>
                <li>Für Sub-Accounts: OKX unterstützt separate Keys pro Sub-Account (Master-Account → API → „Sub-Account API"). Jeden Key hier mit eigener Bezeichnung hinzufügen.</li>
              </ol>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bezeichnung dieses Kontos</Label>
                <Input
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value.slice(0, 40) }))}
                  placeholder={suggestedLabel}
                  data-testid="broker-okx-label"
                  className="mt-1.5"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Frei wählbarer Name zur Unterscheidung (z. B. „Haupt-Konto", „Sub-Konto Scalping", „Sub-Konto Swing"). Leer lassen für „{suggestedLabel}".
                </p>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Region deines OKX-Kontos</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Wichtig: EU/EEA-Konten (deutsche/französische Nutzer:innen, Login über <span className="text-foreground">my.okx.com</span>) müssen „EEA" wählen — sonst antwortet OKX „APIKey does not exist".
                </p>
                <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label="OKX-Region">
                  {[
                    { v: "global", label: "Global (www.okx.com)" },
                    { v: "eea", label: "EEA / EU (eea.okx.com)" },
                    { v: "us", label: "USA (app.okx.com)" },
                  ].map((r) => (
                    <button
                      key={r.v}
                      type="button"
                      role="radio"
                      aria-checked={form.region === r.v}
                      onClick={() => setForm((f) => ({ ...f, region: r.v }))}
                      data-testid={`broker-okx-region-${r.v}`}
                      className={`rounded-full border px-4 h-9 text-sm transition-colors ${
                        form.region === r.v
                          ? "border-primary/60 bg-primary/15 text-foreground"
                          : "border-border bg-secondary/40 text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">API-Key</Label>
                <Input
                  value={form.api_key}
                  onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value.trim() }))}
                  placeholder="z. B. 8b7f1c…"
                  data-testid="broker-okx-apikey"
                  className="mt-1.5 font-mono"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Secret Key</Label>
                <Input
                  type="password"
                  value={form.api_secret}
                  onChange={(e) => setForm((f) => ({ ...f, api_secret: e.target.value.trim() }))}
                  placeholder="wird verschlüsselt gespeichert"
                  data-testid="broker-okx-secret"
                  className="mt-1.5 font-mono"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Passphrase</Label>
                <Input
                  type="password"
                  value={form.passphrase}
                  onChange={(e) => setForm((f) => ({ ...f, passphrase: e.target.value }))}
                  placeholder="beim Key-Erstellen gesetzt"
                  data-testid="broker-okx-passphrase"
                  className="mt-1.5 font-mono"
                />
              </div>
              <div className="flex items-end">
                <ToggleRow
                  label="Demo-Trading"
                  description="Nur ankreuzen, wenn du einen OKX-Demo-Key benutzt."
                  checked={form.demo}
                  onChange={(v) => setForm((f) => ({ ...f, demo: v }))}
                  testid="broker-okx-demo"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <Button
                className="rounded-full h-11 px-5"
                onClick={connect}
                disabled={busy}
                data-testid="broker-okx-connect"
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} /> : <ShieldCheck className="mr-2 h-4 w-4" strokeWidth={1.5} />}
                {busy ? "Wird getestet…" : "Verbinden und testen"}
              </Button>
              {hasAny && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-full"
                  onClick={() => { setShowForm(false); resetForm(); }}
                  disabled={busy}
                  data-testid="broker-okx-cancel-add"
                >
                  Abbrechen
                </Button>
              )}
              <div className="text-xs text-muted-foreground">
                Keys werden AES-verschlüsselt gespeichert (Fernet).
              </div>
            </div>
          </div>
        )}
      </div>
      <QRScanDialog
        open={qrOpen}
        onOpenChange={setQrOpen}
        onResult={(res) => setForm((f) => ({
          ...f,
          api_key: res.api_key || f.api_key,
          api_secret: res.api_secret || f.api_secret,
          passphrase: res.passphrase || f.passphrase,
        }))}
      />
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function KrakenBrokerCard() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [syncAllBusy, setSyncAllBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState({});
  const [qrOpen, setQrOpen] = useState(false);
  const [form, setForm] = useState({ api_key: "", api_secret: "", label: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/broker/kraken/connections`, { withCredentials: true });
      setConnections(Array.isArray(res.data) ? res.data : []);
      if (!res.data || res.data.length === 0) setShowForm(true);
    } catch { setConnections([]); setShowForm(true); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const resetForm = () => setForm({ api_key: "", api_secret: "", label: "" });

  const connect = async () => {
    if (!form.api_key || !form.api_secret) { toast.error("Bitte beide Felder ausfüllen."); return; }
    const defaultLabel = connections.length === 0 ? "Haupt-Konto" : `Sub-Konto ${connections.length + 1}`;
    const label = (form.label || "").trim() || defaultLabel;
    if (connections.some((c) => (c.label || "Haupt-Konto") === label)) {
      toast.error("Bezeichnung bereits vergeben"); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/api/broker/kraken/connect`, { ...form, label }, { withCredentials: true });
      resetForm(); setShowForm(false); await load();
      toast.success(`Kraken „${label}" verbunden`);
    } catch (e) {
      const raw = e?.response?.data?.detail || "Prüfe API-Key und Private Key.";
      toast.error("Verbindung fehlgeschlagen", { description: raw.replace(/^Verbindung fehlgeschlagen:\s*/i, "") });
    } finally { setBusy(false); }
  };
  const disconnectOne = async (label) => {
    if (!window.confirm(`Kraken-Konto „${label}" wirklich trennen?`)) return;
    setRowBusy((m) => ({ ...m, [label]: "disconnect" }));
    try {
      await axios.delete(`${API}/api/broker/kraken/disconnect`, { params: { label }, withCredentials: true });
      toast.success(`„${label}" getrennt`); await load();
    } catch (e) { toast.error("Trennen fehlgeschlagen", { description: e?.response?.data?.detail || "" }); }
    finally { setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; }); }
  };
  const syncOne = async (label) => {
    setRowBusy((m) => ({ ...m, [label]: "sync" }));
    try {
      const res = await axios.post(`${API}/api/broker/kraken/sync`, {}, { params: { label }, withCredentials: true });
      const d = res.data || {};
      toast.success(`Sync „${label}" abgeschlossen`, { description: `${d.inserted || 0} Trades gepaart · ${d.fetched || 0} Fills` });
      await load();
    } catch (e) { toast.error("Sync fehlgeschlagen", { description: e?.response?.data?.detail || "Kraken nicht erreichbar." }); }
    finally { setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; }); }
  };
  const renameOne = async (label) => {
    const raw = window.prompt(`Neue Bezeichnung für „${label}":`, label);
    if (raw === null) return;
    const nextLabel = raw.trim().slice(0, 40);
    if (!nextLabel || nextLabel === label) return;
    setRowBusy((m) => ({ ...m, [label]: "rename" }));
    try {
      await axios.patch(`${API}/api/broker/kraken/connections/${encodeURIComponent(label)}`, { new_label: nextLabel }, { withCredentials: true });
      toast.success(`Umbenannt zu „${nextLabel}"`); await load();
    } catch (e) { toast.error("Umbenennen fehlgeschlagen", { description: e?.response?.data?.detail || "" }); }
    finally { setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; }); }
  };
  const syncAll = async () => {
    setSyncAllBusy(true);
    try {
      const res = await axios.post(`${API}/api/broker/kraken/sync`, {}, { withCredentials: true });
      const d = res.data || {};
      if (d.multi) toast.success(`Alle ${d.accounts_synced} Sub-Accounts synchronisiert`, { description: `${d.inserted || 0} Trades gepaart · ${d.fetched || 0} Fills` });
      else toast.success(`Sync „${d.label || ""}" abgeschlossen`, { description: `${d.inserted || 0} Trades · ${d.fetched || 0} Fills` });
      await load();
    } catch (e) { toast.error("Sync fehlgeschlagen", { description: e?.response?.data?.detail || "Kraken nicht erreichbar." }); }
    finally { setSyncAllBusy(false); }
  };

  if (loading) return <div className="card-surface p-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} /></div>;
  const hasAny = connections.length > 0;
  const suggestedLabel = connections.length === 0 ? "Haupt-Konto" : `Sub-Konto ${connections.length + 1}`;

  return (
    <div className="card-surface p-6 max-w-3xl" data-testid="broker-kraken-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-secondary border border-border grid place-items-center">
            <Plug className="h-5 w-5 text-primary-foreground/80" strokeWidth={1.5} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading text-lg">Kraken</h2>
              <Badge variant="secondary" className={`rounded-full text-xs ${hasAny ? "bg-[hsl(122_39%_49%/0.15)] text-[hsl(122_39%_65%)] border border-[hsl(122_39%_49%/0.35)]" : "bg-secondary border border-border"}`}>
                {hasAny ? `${connections.length} ${connections.length === 1 ? "Konto verbunden" : "Konten verbunden"}` : "nicht verbunden"}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Krypto-Börse Kraken (Spot). Kein Demo-Modus für Spot verfügbar. Mehrere Sub-Accounts möglich.</p>
          </div>
        </div>
        {hasAny && connections.length > 1 && (
          <Button variant="outline" size="sm" className="rounded-full" onClick={syncAll} disabled={syncAllBusy} data-testid="broker-kraken-sync-all">
            <RefreshCw className={`mr-2 h-4 w-4 ${syncAllBusy ? "animate-spin" : ""}`} strokeWidth={1.5} />
            {syncAllBusy ? "Sync läuft…" : "Alle synchronisieren"}
          </Button>
        )}
      </div>
      {hasAny && (
        <div className="mt-5 space-y-3">
          {connections.map((c) => {
            const label = c.label || "Haupt-Konto";
            const slug = label.replace(/\s+/g, "-").toLowerCase();
            const isSync = rowBusy[label] === "sync";
            const isDisc = rowBusy[label] === "disconnect";
            return (
              <div key={label} className="rounded-xl border border-border bg-secondary/30 p-4 flex items-start justify-between gap-4 flex-wrap" data-testid={`broker-kraken-conn-${slug}`}>
                <div className="flex items-start gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-background border border-border grid place-items-center shrink-0">
                    <Layers className="h-4 w-4 text-primary-foreground/70" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <span className="font-medium text-sm">{label}</span>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      Key {c.api_key_mask} · {c.last_sync_at ? `letzter Sync ${new Date(c.last_sync_at).toLocaleString("de-DE")}` : "noch nicht synchronisiert"}
                      {typeof c.last_sync_count === "number" && c.last_sync_count > 0 && ` · ${c.last_sync_count} Trades`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => syncOne(label)} disabled={isSync || isDisc || syncAllBusy} data-testid={`broker-kraken-sync-${slug}`}>
                    <RefreshCw className={`mr-2 h-4 w-4 ${isSync ? "animate-spin" : ""}`} strokeWidth={1.5} />
                    {isSync ? "Sync läuft…" : "Sync"}
                  </Button>
                  <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" onClick={() => renameOne(label)} disabled={isSync || isDisc || rowBusy[label] === "rename"} title="Umbenennen" data-testid={`broker-kraken-rename-${slug}`}>
                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-[hsl(0_66%_72%)]" onClick={() => disconnectOne(label)} disabled={isSync || isDisc} data-testid={`broker-kraken-disconnect-${slug}`}>
                    <Trash2 className="mr-2 h-4 w-4" strokeWidth={1.5} /> Trennen
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {hasAny && !showForm && (
        <div className="mt-4">
          <Button variant="outline" className="rounded-full" size="sm" onClick={() => setShowForm(true)} data-testid="broker-kraken-add-account">
            <Coins className="mr-2 h-4 w-4" strokeWidth={1.5} /> Weiteres Kraken-Konto hinzufügen
          </Button>
        </div>
      )}
      {showForm && (
        <div className="mt-6 space-y-4 border-t border-border pt-5">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs text-muted-foreground">
              Bei Kraken: Settings → API → Add key → nur „Query Funds" + „Query Closed Orders & Trades" ankreuzen. Der „Private Key" ist ein Base64-String (endet oft mit „==").
            </p>
            <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => setQrOpen(true)} data-testid="broker-kraken-scan-qr">
              <QrCode className="mr-2 h-4 w-4" strokeWidth={1.5} /> QR-Code scannen
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bezeichnung dieses Kontos</Label>
              <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value.slice(0, 40) }))} placeholder={suggestedLabel} data-testid="broker-kraken-label" className="mt-1.5" />
              <p className="text-[11px] text-muted-foreground mt-1">Frei wählbarer Name. Leer lassen für „{suggestedLabel}".</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">API-Key</Label>
              <Input value={form.api_key} onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value.trim() }))} data-testid="broker-kraken-apikey" className="mt-1.5 font-mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Private Key (Base64)</Label>
              <Input type="password" value={form.api_secret} onChange={(e) => setForm((f) => ({ ...f, api_secret: e.target.value.trim() }))} data-testid="broker-kraken-secret" className="mt-1.5 font-mono" />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <Button className="rounded-full h-11 px-5" onClick={connect} disabled={busy} data-testid="broker-kraken-connect">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} /> : <ShieldCheck className="mr-2 h-4 w-4" strokeWidth={1.5} />}
              {busy ? "Wird getestet…" : "Verbinden und testen"}
            </Button>
            {hasAny && (
              <Button variant="ghost" size="sm" className="rounded-full" onClick={() => { setShowForm(false); resetForm(); }} disabled={busy} data-testid="broker-kraken-cancel-add">Abbrechen</Button>
            )}
            <div className="text-xs text-muted-foreground">Keys werden AES-verschlüsselt (Fernet).</div>
          </div>
        </div>
      )}
      <QRScanDialog open={qrOpen} onOpenChange={setQrOpen}
        onResult={(res) => setForm((f) => ({ ...f, api_key: res.api_key || f.api_key, api_secret: res.api_secret || f.api_secret }))} />
    </div>
  );
}

function BinanceBrokerCard() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [syncAllBusy, setSyncAllBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState({});
  const [qrOpen, setQrOpen] = useState(false);
  const [form, setForm] = useState({ api_key: "", api_secret: "", symbols: "", demo: false, label: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/broker/binance/connections`, { withCredentials: true });
      setConnections(Array.isArray(res.data) ? res.data : []);
      if (!res.data || res.data.length === 0) setShowForm(true);
    } catch { setConnections([]); setShowForm(true); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const resetForm = () => setForm({ api_key: "", api_secret: "", symbols: "", demo: false, label: "" });

  const connect = async () => {
    if (!form.api_key || !form.api_secret) { toast.error("Bitte API-Key und Secret ausfüllen."); return; }
    const defaultLabel = connections.length === 0 ? "Haupt-Konto" : `Sub-Konto ${connections.length + 1}`;
    const label = (form.label || "").trim() || defaultLabel;
    if (connections.some((c) => (c.label || "Haupt-Konto") === label)) {
      toast.error("Bezeichnung bereits vergeben"); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/api/broker/binance/connect`, { ...form, label }, { withCredentials: true });
      resetForm(); setShowForm(false); await load();
      toast.success(`Binance „${label}" verbunden`);
    } catch (e) {
      const raw = e?.response?.data?.detail || "Prüfe API-Key und Secret.";
      toast.error("Verbindung fehlgeschlagen", { description: raw.replace(/^Verbindung fehlgeschlagen:\s*/i, "") });
    } finally { setBusy(false); }
  };
  const disconnectOne = async (label) => {
    if (!window.confirm(`Binance-Konto „${label}" wirklich trennen?`)) return;
    setRowBusy((m) => ({ ...m, [label]: "disconnect" }));
    try {
      await axios.delete(`${API}/api/broker/binance/disconnect`, { params: { label }, withCredentials: true });
      toast.success(`„${label}" getrennt`); await load();
    } catch (e) { toast.error("Trennen fehlgeschlagen", { description: e?.response?.data?.detail || "" }); }
    finally { setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; }); }
  };
  const syncOne = async (label) => {
    setRowBusy((m) => ({ ...m, [label]: "sync" }));
    try {
      const res = await axios.post(`${API}/api/broker/binance/sync`, {}, { params: { label }, withCredentials: true });
      const d = res.data || {};
      toast.success(`Sync „${label}" abgeschlossen`, { description: `${d.inserted || 0} Trades gepaart · ${d.fetched || 0} Fills` });
      await load();
    } catch (e) { toast.error("Sync fehlgeschlagen", { description: e?.response?.data?.detail || "Binance nicht erreichbar." }); }
    finally { setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; }); }
  };
  const renameOne = async (label) => {
    const raw = window.prompt(`Neue Bezeichnung für „${label}":`, label);
    if (raw === null) return;
    const nextLabel = raw.trim().slice(0, 40);
    if (!nextLabel || nextLabel === label) return;
    setRowBusy((m) => ({ ...m, [label]: "rename" }));
    try {
      await axios.patch(`${API}/api/broker/binance/connections/${encodeURIComponent(label)}`, { new_label: nextLabel }, { withCredentials: true });
      toast.success(`Umbenannt zu „${nextLabel}"`); await load();
    } catch (e) { toast.error("Umbenennen fehlgeschlagen", { description: e?.response?.data?.detail || "" }); }
    finally { setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; }); }
  };
  const syncAll = async () => {
    setSyncAllBusy(true);
    try {
      const res = await axios.post(`${API}/api/broker/binance/sync`, {}, { withCredentials: true });
      const d = res.data || {};
      if (d.multi) toast.success(`Alle ${d.accounts_synced} Sub-Accounts synchronisiert`, { description: `${d.inserted || 0} Trades gepaart · ${d.fetched || 0} Fills` });
      else toast.success(`Sync „${d.label || ""}" abgeschlossen`, { description: `${d.inserted || 0} Trades · ${d.fetched || 0} Fills` });
      await load();
    } catch (e) { toast.error("Sync fehlgeschlagen", { description: e?.response?.data?.detail || "Binance nicht erreichbar." }); }
    finally { setSyncAllBusy(false); }
  };

  if (loading) return <div className="card-surface p-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} /></div>;
  const hasAny = connections.length > 0;
  const suggestedLabel = connections.length === 0 ? "Haupt-Konto" : `Sub-Konto ${connections.length + 1}`;

  return (
    <div className="card-surface p-6 max-w-3xl" data-testid="broker-binance-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-secondary border border-border grid place-items-center">
            <Plug className="h-5 w-5 text-primary-foreground/80" strokeWidth={1.5} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading text-lg">Binance</h2>
              <Badge variant="secondary" className={`rounded-full text-xs ${hasAny ? "bg-[hsl(122_39%_49%/0.15)] text-[hsl(122_39%_65%)] border border-[hsl(122_39%_49%/0.35)]" : "bg-secondary border border-border"}`}>
                {hasAny ? `${connections.length} ${connections.length === 1 ? "Konto verbunden" : "Konten verbunden"}` : "nicht verbunden"}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Krypto-Börse Binance (USDT-M Futures). Read-only API. Mehrere Sub-Accounts möglich.</p>
          </div>
        </div>
        {hasAny && connections.length > 1 && (
          <Button variant="outline" size="sm" className="rounded-full" onClick={syncAll} disabled={syncAllBusy} data-testid="broker-binance-sync-all">
            <RefreshCw className={`mr-2 h-4 w-4 ${syncAllBusy ? "animate-spin" : ""}`} strokeWidth={1.5} />
            {syncAllBusy ? "Sync läuft…" : "Alle synchronisieren"}
          </Button>
        )}
      </div>
      {hasAny && (
        <div className="mt-5 space-y-3">
          {connections.map((c) => {
            const label = c.label || "Haupt-Konto";
            const slug = label.replace(/\s+/g, "-").toLowerCase();
            const isSync = rowBusy[label] === "sync";
            const isDisc = rowBusy[label] === "disconnect";
            return (
              <div key={label} className="rounded-xl border border-border bg-secondary/30 p-4 flex items-start justify-between gap-4 flex-wrap" data-testid={`broker-binance-conn-${slug}`}>
                <div className="flex items-start gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-background border border-border grid place-items-center shrink-0">
                    <Layers className="h-4 w-4 text-primary-foreground/70" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{label}</span>
                      {c.demo && <Badge variant="secondary" className="rounded-full text-[10px] bg-[hsl(32_60%_55%/0.15)] text-[hsl(32_60%_72%)] border border-[hsl(32_60%_55%/0.4)]">Testnet</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      Key {c.api_key_mask} · Paare {c.symbols || "Default"} · {c.last_sync_at ? `letzter Sync ${new Date(c.last_sync_at).toLocaleString("de-DE")}` : "noch nicht synchronisiert"}
                      {typeof c.last_sync_count === "number" && c.last_sync_count > 0 && ` · ${c.last_sync_count} Trades`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => syncOne(label)} disabled={isSync || isDisc || syncAllBusy} data-testid={`broker-binance-sync-${slug}`}>
                    <RefreshCw className={`mr-2 h-4 w-4 ${isSync ? "animate-spin" : ""}`} strokeWidth={1.5} />
                    {isSync ? "Sync läuft…" : "Sync"}
                  </Button>
                  <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" onClick={() => renameOne(label)} disabled={isSync || isDisc || rowBusy[label] === "rename"} title="Umbenennen" data-testid={`broker-binance-rename-${slug}`}>
                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-[hsl(0_66%_72%)]" onClick={() => disconnectOne(label)} disabled={isSync || isDisc} data-testid={`broker-binance-disconnect-${slug}`}>
                    <Trash2 className="mr-2 h-4 w-4" strokeWidth={1.5} /> Trennen
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {hasAny && !showForm && (
        <div className="mt-4">
          <Button variant="outline" className="rounded-full" size="sm" onClick={() => setShowForm(true)} data-testid="broker-binance-add-account">
            <Coins className="mr-2 h-4 w-4" strokeWidth={1.5} /> Weiteres Binance-Konto hinzufügen
          </Button>
        </div>
      )}
      {showForm && (
        <div className="mt-6 space-y-4 border-t border-border pt-5">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs text-muted-foreground">
              Bei Binance: Profil → API-Verwaltung → Create API → nur „Enable Reading" ankreuzen → IP-Whitelist optional. Bei Demo: Key auf <span className="text-foreground">testnet.binance.vision</span> erstellen.
            </p>
            <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => setQrOpen(true)} data-testid="broker-binance-scan-qr">
              <QrCode className="mr-2 h-4 w-4" strokeWidth={1.5} /> QR-Code scannen
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bezeichnung dieses Kontos</Label>
              <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value.slice(0, 40) }))} placeholder={suggestedLabel} data-testid="broker-binance-label" className="mt-1.5" />
              <p className="text-[11px] text-muted-foreground mt-1">Frei wählbarer Name. Leer lassen für „{suggestedLabel}".</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">API-Key</Label>
              <Input value={form.api_key} onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value.trim() }))} data-testid="broker-binance-apikey" className="mt-1.5 font-mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Secret Key</Label>
              <Input type="password" value={form.api_secret} onChange={(e) => setForm((f) => ({ ...f, api_secret: e.target.value.trim() }))} data-testid="broker-binance-secret" className="mt-1.5 font-mono" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Trading-Paare (kommagetrennt)</Label>
              <Input value={form.symbols} onChange={(e) => setForm((f) => ({ ...f, symbols: e.target.value }))} placeholder="z. B. BTCUSDT, ETHUSDT, SOLUSDT" data-testid="broker-binance-symbols" className="mt-1.5 font-mono" />
              <p className="text-[11px] text-muted-foreground mt-1">Binance liefert Trades nur pro Paar. Leer lassen für BTC/ETH/SOL/BNB gegen USDT.</p>
            </div>
            <div className="flex items-end md:col-span-2">
              <ToggleRow label="Testnet (Demo)" description="Nur ankreuzen, wenn du Binance-Testnet-Keys nutzt." checked={form.demo} onChange={(v) => setForm((f) => ({ ...f, demo: v }))} testid="broker-binance-demo" />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <Button className="rounded-full h-11 px-5" onClick={connect} disabled={busy} data-testid="broker-binance-connect">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} /> : <ShieldCheck className="mr-2 h-4 w-4" strokeWidth={1.5} />}
              {busy ? "Wird getestet…" : "Verbinden und testen"}
            </Button>
            {hasAny && (
              <Button variant="ghost" size="sm" className="rounded-full" onClick={() => { setShowForm(false); resetForm(); }} disabled={busy} data-testid="broker-binance-cancel-add">Abbrechen</Button>
            )}
            <div className="text-xs text-muted-foreground">Keys werden AES-verschlüsselt (Fernet).</div>
          </div>
        </div>
      )}
      <QRScanDialog open={qrOpen} onOpenChange={setQrOpen}
        onResult={(res) => setForm((f) => ({ ...f, api_key: res.api_key || f.api_key, api_secret: res.api_secret || f.api_secret }))} />
    </div>
  );
}

function BitgetBrokerCard() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [syncAllBusy, setSyncAllBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState({});
  const [qrOpen, setQrOpen] = useState(false);
  const [form, setForm] = useState({ api_key: "", api_secret: "", passphrase: "", demo: false, label: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/broker/bitget/connections`, { withCredentials: true });
      setConnections(Array.isArray(res.data) ? res.data : []);
      if (!res.data || res.data.length === 0) setShowForm(true);
    } catch {
      setConnections([]);
      setShowForm(true);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({ api_key: "", api_secret: "", passphrase: "", demo: false, label: "" });

  const connect = async () => {
    if (!form.api_key || !form.api_secret || !form.passphrase) {
      toast.error("Bitte alle drei Felder ausfüllen."); return;
    }
    const defaultLabel = connections.length === 0 ? "Haupt-Konto" : `Sub-Konto ${connections.length + 1}`;
    const label = (form.label || "").trim() || defaultLabel;
    if (connections.some((c) => (c.label || "Haupt-Konto") === label)) {
      toast.error("Bezeichnung bereits vergeben", { description: "Bitte wähle einen anderen Namen für dieses Sub-Konto." });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/api/broker/bitget/connect`, { ...form, label }, { withCredentials: true });
      resetForm();
      setShowForm(false);
      await load();
      toast.success(`Bitget „${label}" verbunden`);
    } catch (e) {
      const raw = e?.response?.data?.detail || "Prüfe API-Key, Secret und Passphrase.";
      toast.error("Verbindung fehlgeschlagen", { description: raw.replace(/^Verbindung fehlgeschlagen:\s*/i, "") });
    } finally { setBusy(false); }
  };

  const disconnectOne = async (label) => {
    if (!window.confirm(`Bitget-Konto „${label}" wirklich trennen? Zugehörige API-Trades werden entfernt (manuell verknüpfte bleiben erhalten).`)) return;
    setRowBusy((m) => ({ ...m, [label]: "disconnect" }));
    try {
      await axios.delete(`${API}/api/broker/bitget/disconnect`, { params: { label }, withCredentials: true });
      toast.success(`„${label}" getrennt`);
      await load();
    } catch (e) {
      toast.error("Trennen fehlgeschlagen", { description: e?.response?.data?.detail || "" });
    } finally {
      setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; });
    }
  };

  const renameOne = async (label) => {
    const raw = window.prompt(`Neue Bezeichnung für „${label}":`, label);
    if (raw === null) return;
    const nextLabel = raw.trim().slice(0, 40);
    if (!nextLabel || nextLabel === label) return;
    setRowBusy((m) => ({ ...m, [label]: "rename" }));
    try {
      await axios.patch(
        `${API}/api/broker/bitget/connections/${encodeURIComponent(label)}`,
        { new_label: nextLabel },
        { withCredentials: true },
      );
      toast.success(`Umbenannt zu „${nextLabel}"`);
      await load();
    } catch (e) {
      toast.error("Umbenennen fehlgeschlagen", { description: e?.response?.data?.detail || "" });
    } finally {
      setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; });
    }
  };


  const syncOne = async (label) => {
    setRowBusy((m) => ({ ...m, [label]: "sync" }));
    try {
      const res = await axios.post(`${API}/api/broker/bitget/sync`, {}, { params: { label }, withCredentials: true });
      const d = res.data || {};
      toast.success(`Sync „${label}" abgeschlossen`, {
        description: `${d.inserted || 0} Trades gepaart · ${d.fetched || 0} Fills gelesen`,
      });
      await load();
    } catch (e) {
      toast.error("Sync fehlgeschlagen", { description: e?.response?.data?.detail || "Bitget nicht erreichbar." });
    } finally {
      setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; });
    }
  };

  const syncAll = async () => {
    setSyncAllBusy(true);
    try {
      const res = await axios.post(`${API}/api/broker/bitget/sync`, {}, { withCredentials: true });
      const d = res.data || {};
      if (d.multi) {
        toast.success(`Alle ${d.accounts_synced} Sub-Accounts synchronisiert`, {
          description: `${d.inserted || 0} Trades gepaart · ${d.fetched || 0} Fills gelesen`,
        });
      } else {
        toast.success(`Sync „${d.label || ""}" abgeschlossen`, {
          description: `${d.inserted || 0} Trades gepaart · ${d.fetched || 0} Fills`,
        });
      }
      await load();
    } catch (e) {
      toast.error("Sync fehlgeschlagen", { description: e?.response?.data?.detail || "Bitget nicht erreichbar." });
    } finally { setSyncAllBusy(false); }
  };

  if (loading) return <div className="card-surface p-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} /></div>;
  const hasAny = connections.length > 0;
  const suggestedLabel = connections.length === 0 ? "Haupt-Konto" : `Sub-Konto ${connections.length + 1}`;

  return (
    <div className="card-surface p-6 max-w-3xl" data-testid="broker-bitget-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-secondary border border-border grid place-items-center">
            <Plug className="h-5 w-5 text-primary-foreground/80" strokeWidth={1.5} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading text-lg">Bitget</h2>
              <Badge variant="secondary" className={`rounded-full text-xs ${
                hasAny
                  ? "bg-[hsl(122_39%_49%/0.15)] text-[hsl(122_39%_65%)] border border-[hsl(122_39%_49%/0.35)]"
                  : "bg-secondary border border-border"
              }`}>
                {hasAny ? `${connections.length} ${connections.length === 1 ? "Konto verbunden" : "Konten verbunden"}` : "nicht verbunden"}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Krypto-Börse Bitget (USDT-Futures). Read-only API — du kannst mehrere Sub-Accounts hinzufügen.
            </p>
          </div>
        </div>
        {hasAny && connections.length > 1 && (
          <Button variant="outline" size="sm" className="rounded-full" onClick={syncAll} disabled={syncAllBusy} data-testid="broker-bitget-sync-all">
            <RefreshCw className={`mr-2 h-4 w-4 ${syncAllBusy ? "animate-spin" : ""}`} strokeWidth={1.5} />
            {syncAllBusy ? "Sync läuft…" : "Alle synchronisieren"}
          </Button>
        )}
      </div>

      {hasAny && (
        <div className="mt-5 space-y-3">
          {connections.map((c) => {
            const label = c.label || "Haupt-Konto";
            const slug = label.replace(/\s+/g, "-").toLowerCase();
            const isSync = rowBusy[label] === "sync";
            const isDisc = rowBusy[label] === "disconnect";
            return (
              <div
                key={label}
                className="rounded-xl border border-border bg-secondary/30 p-4 flex items-start justify-between gap-4 flex-wrap"
                data-testid={`broker-bitget-conn-${slug}`}
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-background border border-border grid place-items-center shrink-0">
                    <Layers className="h-4 w-4 text-primary-foreground/70" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{label}</span>
                      {c.demo && (
                        <Badge variant="secondary" className="rounded-full text-[10px] bg-[hsl(32_60%_55%/0.15)] text-[hsl(32_60%_72%)] border border-[hsl(32_60%_55%/0.4)]">
                          Paper
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      Key {c.api_key_mask} · {c.last_sync_at ? `letzter Sync ${new Date(c.last_sync_at).toLocaleString("de-DE")}` : "noch nicht synchronisiert"}
                      {typeof c.last_sync_count === "number" && c.last_sync_count > 0 && ` · ${c.last_sync_count} Trades`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => syncOne(label)} disabled={isSync || isDisc || syncAllBusy} data-testid={`broker-bitget-sync-${slug}`}>
                    <RefreshCw className={`mr-2 h-4 w-4 ${isSync ? "animate-spin" : ""}`} strokeWidth={1.5} />
                    {isSync ? "Sync läuft…" : "Sync"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full h-8 w-8"
                    onClick={() => renameOne(label)}
                    disabled={isSync || isDisc || rowBusy[label] === "rename"}
                    title="Umbenennen"
                    data-testid={`broker-bitget-rename-${slug}`}
                  >
                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-[hsl(0_66%_72%)]" onClick={() => disconnectOne(label)} disabled={isSync || isDisc} data-testid={`broker-bitget-disconnect-${slug}`}>
                    <Trash2 className="mr-2 h-4 w-4" strokeWidth={1.5} /> Trennen
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasAny && !showForm && (
        <div className="mt-4">
          <Button variant="outline" className="rounded-full" size="sm" onClick={() => setShowForm(true)} data-testid="broker-bitget-add-account">
            <Coins className="mr-2 h-4 w-4" strokeWidth={1.5} /> Weiteres Bitget-Konto hinzufügen
          </Button>
        </div>
      )}

      {showForm && (
        <div className="mt-6 space-y-4 border-t border-border pt-5">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs text-muted-foreground">
              Bei Bitget: Profil → API-Verwaltung → Neuer API-Key → nur „Read"-Rechte setzen → IP-Whitelist leer lassen.
            </p>
            <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => setQrOpen(true)} data-testid="broker-bitget-scan-qr">
              <QrCode className="mr-2 h-4 w-4" strokeWidth={1.5} /> QR-Code scannen
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bezeichnung dieses Kontos</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value.slice(0, 40) }))}
                placeholder={suggestedLabel}
                data-testid="broker-bitget-label"
                className="mt-1.5"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Frei wählbarer Name zur Unterscheidung (z. B. „Haupt-Konto", „Sub-Konto Scalping"). Leer lassen für „{suggestedLabel}".
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">API-Key</Label>
              <Input value={form.api_key} onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value.trim() }))} placeholder="bg_…" data-testid="broker-bitget-apikey" className="mt-1.5 font-mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Secret Key</Label>
              <Input type="password" value={form.api_secret} onChange={(e) => setForm((f) => ({ ...f, api_secret: e.target.value.trim() }))} data-testid="broker-bitget-secret" className="mt-1.5 font-mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Passphrase</Label>
              <Input type="password" value={form.passphrase} onChange={(e) => setForm((f) => ({ ...f, passphrase: e.target.value }))} placeholder="beim Key-Erstellen gesetzt" data-testid="broker-bitget-passphrase" className="mt-1.5 font-mono" />
            </div>
            <div className="flex items-end">
              <ToggleRow
                label="Demo-Trading (Paper)"
                description="Nur ankreuzen, wenn du einen Bitget-Paper-Trading-Key nutzt."
                checked={form.demo}
                onChange={(v) => setForm((f) => ({ ...f, demo: v }))}
                testid="broker-bitget-demo"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <Button className="rounded-full h-11 px-5" onClick={connect} disabled={busy} data-testid="broker-bitget-connect">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} /> : <ShieldCheck className="mr-2 h-4 w-4" strokeWidth={1.5} />}
              {busy ? "Wird getestet…" : "Verbinden und testen"}
            </Button>
            {hasAny && (
              <Button variant="ghost" size="sm" className="rounded-full" onClick={() => { setShowForm(false); resetForm(); }} disabled={busy} data-testid="broker-bitget-cancel-add">
                Abbrechen
              </Button>
            )}
            <div className="text-xs text-muted-foreground">Keys werden AES-verschlüsselt (Fernet).</div>
          </div>
        </div>
      )}
      <QRScanDialog
        open={qrOpen}
        onOpenChange={setQrOpen}
        onResult={(res) => setForm((f) => ({
          ...f,
          api_key: res.api_key || f.api_key,
          api_secret: res.api_secret || f.api_secret,
          passphrase: res.passphrase || f.passphrase,
        }))}
      />
    </div>
  );
}

function BitunixBrokerCard() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [syncAllBusy, setSyncAllBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState({});
  const [qrOpen, setQrOpen] = useState(false);
  const [form, setForm] = useState({ api_key: "", api_secret: "", symbols: "", label: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/broker/bitunix/connections`, { withCredentials: true });
      setConnections(Array.isArray(res.data) ? res.data : []);
      if (!res.data || res.data.length === 0) setShowForm(true);
    } catch { setConnections([]); setShowForm(true); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const resetForm = () => setForm({ api_key: "", api_secret: "", symbols: "", label: "" });

  const connect = async () => {
    if (!form.api_key || !form.api_secret) { toast.error("Bitte API-Key und Secret ausfüllen."); return; }
    const defaultLabel = connections.length === 0 ? "Haupt-Konto" : `Sub-Konto ${connections.length + 1}`;
    const label = (form.label || "").trim() || defaultLabel;
    if (connections.some((c) => (c.label || "Haupt-Konto") === label)) {
      toast.error("Bezeichnung bereits vergeben"); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/api/broker/bitunix/connect`, { ...form, label }, { withCredentials: true });
      resetForm(); setShowForm(false); await load();
      toast.success(`Bitunix „${label}" verbunden`);
    } catch (e) {
      const raw = e?.response?.data?.detail || "Prüfe API-Key und Secret.";
      toast.error("Verbindung fehlgeschlagen", { description: raw.replace(/^Verbindung fehlgeschlagen:\s*/i, "") });
    } finally { setBusy(false); }
  };
  const disconnectOne = async (label) => {
    if (!window.confirm(`Bitunix-Konto „${label}" wirklich trennen?`)) return;
    setRowBusy((m) => ({ ...m, [label]: "disconnect" }));
    try {
      await axios.delete(`${API}/api/broker/bitunix/disconnect`, { params: { label }, withCredentials: true });
      toast.success(`„${label}" getrennt`); await load();
    } catch (e) { toast.error("Trennen fehlgeschlagen", { description: e?.response?.data?.detail || "" }); }
    finally { setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; }); }
  };
  const syncOne = async (label) => {
    setRowBusy((m) => ({ ...m, [label]: "sync" }));
    try {
      const res = await axios.post(`${API}/api/broker/bitunix/sync`, {}, { params: { label }, withCredentials: true });
      const d = res.data || {};
      toast.success(`Sync „${label}" abgeschlossen`, { description: `${d.inserted || 0} Trades gepaart · ${d.fetched || 0} Fills` });
      await load();
    } catch (e) { toast.error("Sync fehlgeschlagen", { description: e?.response?.data?.detail || "Bitunix nicht erreichbar." }); }
    finally { setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; }); }
  };
  const renameOne = async (label) => {
    const raw = window.prompt(`Neue Bezeichnung für „${label}":`, label);
    if (raw === null) return;
    const nextLabel = raw.trim().slice(0, 40);
    if (!nextLabel || nextLabel === label) return;
    setRowBusy((m) => ({ ...m, [label]: "rename" }));
    try {
      await axios.patch(`${API}/api/broker/bitunix/connections/${encodeURIComponent(label)}`, { new_label: nextLabel }, { withCredentials: true });
      toast.success(`Umbenannt zu „${nextLabel}"`); await load();
    } catch (e) { toast.error("Umbenennen fehlgeschlagen", { description: e?.response?.data?.detail || "" }); }
    finally { setRowBusy((m) => { const n = { ...m }; delete n[label]; return n; }); }
  };
  const syncAll = async () => {
    setSyncAllBusy(true);
    try {
      const res = await axios.post(`${API}/api/broker/bitunix/sync`, {}, { withCredentials: true });
      const d = res.data || {};
      if (d.multi) toast.success(`Alle ${d.accounts_synced} Sub-Accounts synchronisiert`, { description: `${d.inserted || 0} Trades gepaart · ${d.fetched || 0} Fills` });
      else toast.success(`Sync „${d.label || ""}" abgeschlossen`, { description: `${d.inserted || 0} Trades · ${d.fetched || 0} Fills` });
      await load();
    } catch (e) { toast.error("Sync fehlgeschlagen", { description: e?.response?.data?.detail || "Bitunix nicht erreichbar." }); }
    finally { setSyncAllBusy(false); }
  };

  if (loading) return <div className="card-surface p-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} /></div>;
  const hasAny = connections.length > 0;
  const suggestedLabel = connections.length === 0 ? "Haupt-Konto" : `Sub-Konto ${connections.length + 1}`;

  return (
    <div className="card-surface p-6 max-w-3xl" data-testid="broker-bitunix-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-secondary border border-border grid place-items-center">
            <Plug className="h-5 w-5 text-primary-foreground/80" strokeWidth={1.5} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading text-lg">Bitunix</h2>
              <Badge variant="secondary" className={`rounded-full text-xs ${hasAny ? "bg-[hsl(122_39%_49%/0.15)] text-[hsl(122_39%_65%)] border border-[hsl(122_39%_49%/0.35)]" : "bg-secondary border border-border"}`}>
                {hasAny ? `${connections.length} ${connections.length === 1 ? "Konto verbunden" : "Konten verbunden"}` : "nicht verbunden"}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Krypto-Börse Bitunix (Futures). Read-only API — kein Passphrase, kein öffentlicher Testnet-Endpoint. Mehrere Sub-Accounts möglich.</p>
          </div>
        </div>
        {hasAny && connections.length > 1 && (
          <Button variant="outline" size="sm" className="rounded-full" onClick={syncAll} disabled={syncAllBusy} data-testid="broker-bitunix-sync-all">
            <RefreshCw className={`mr-2 h-4 w-4 ${syncAllBusy ? "animate-spin" : ""}`} strokeWidth={1.5} />
            {syncAllBusy ? "Sync läuft…" : "Alle synchronisieren"}
          </Button>
        )}
      </div>
      {hasAny && (
        <div className="mt-5 space-y-3">
          {connections.map((c) => {
            const label = c.label || "Haupt-Konto";
            const slug = label.replace(/\s+/g, "-").toLowerCase();
            const isSync = rowBusy[label] === "sync";
            const isDisc = rowBusy[label] === "disconnect";
            return (
              <div key={label} className="rounded-xl border border-border bg-secondary/30 p-4 flex items-start justify-between gap-4 flex-wrap" data-testid={`broker-bitunix-conn-${slug}`}>
                <div className="flex items-start gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-background border border-border grid place-items-center shrink-0">
                    <Layers className="h-4 w-4 text-primary-foreground/70" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <span className="font-medium text-sm">{label}</span>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      Key {c.api_key_mask} · Paare {c.symbols || "Default"} · {c.last_sync_at ? `letzter Sync ${new Date(c.last_sync_at).toLocaleString("de-DE")}` : "noch nicht synchronisiert"}
                      {typeof c.last_sync_count === "number" && c.last_sync_count > 0 && ` · ${c.last_sync_count} Trades`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => syncOne(label)} disabled={isSync || isDisc || syncAllBusy} data-testid={`broker-bitunix-sync-${slug}`}>
                    <RefreshCw className={`mr-2 h-4 w-4 ${isSync ? "animate-spin" : ""}`} strokeWidth={1.5} />
                    {isSync ? "Sync läuft…" : "Sync"}
                  </Button>
                  <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" onClick={() => renameOne(label)} disabled={isSync || isDisc || rowBusy[label] === "rename"} title="Umbenennen" data-testid={`broker-bitunix-rename-${slug}`}>
                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-[hsl(0_66%_72%)]" onClick={() => disconnectOne(label)} disabled={isSync || isDisc} data-testid={`broker-bitunix-disconnect-${slug}`}>
                    <Trash2 className="mr-2 h-4 w-4" strokeWidth={1.5} /> Trennen
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {hasAny && !showForm && (
        <div className="mt-4">
          <Button variant="outline" className="rounded-full" size="sm" onClick={() => setShowForm(true)} data-testid="broker-bitunix-add-account">
            <Coins className="mr-2 h-4 w-4" strokeWidth={1.5} /> Weiteres Bitunix-Konto hinzufügen
          </Button>
        </div>
      )}
      {showForm && (
        <div className="mt-6 space-y-4 border-t border-border pt-5">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs text-muted-foreground">Bei Bitunix: Profil → API Management → Create API → nur „Read"-Rechte für Futures. Kein Passphrase nötig.</p>
            <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => setQrOpen(true)} data-testid="broker-bitunix-scan-qr">
              <QrCode className="mr-2 h-4 w-4" strokeWidth={1.5} /> QR-Code scannen
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bezeichnung dieses Kontos</Label>
              <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value.slice(0, 40) }))} placeholder={suggestedLabel} data-testid="broker-bitunix-label" className="mt-1.5" />
              <p className="text-[11px] text-muted-foreground mt-1">Frei wählbarer Name. Leer lassen für „{suggestedLabel}".</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">API-Key</Label>
              <Input value={form.api_key} onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value.trim() }))} data-testid="broker-bitunix-apikey" className="mt-1.5 font-mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Secret Key</Label>
              <Input type="password" value={form.api_secret} onChange={(e) => setForm((f) => ({ ...f, api_secret: e.target.value.trim() }))} data-testid="broker-bitunix-secret" className="mt-1.5 font-mono" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Futures-Paare (kommagetrennt)</Label>
              <Input value={form.symbols} onChange={(e) => setForm((f) => ({ ...f, symbols: e.target.value }))} placeholder="z. B. BTCUSDT, ETHUSDT, SOLUSDT" data-testid="broker-bitunix-symbols" className="mt-1.5 font-mono" />
              <p className="text-[11px] text-muted-foreground mt-1">Bitunix liefert Trade-Historie pro Paar. Leer lassen für BTC/ETH/SOL gegen USDT.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <Button className="rounded-full h-11 px-5" onClick={connect} disabled={busy} data-testid="broker-bitunix-connect">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} /> : <ShieldCheck className="mr-2 h-4 w-4" strokeWidth={1.5} />}
              {busy ? "Wird getestet…" : "Verbinden und testen"}
            </Button>
            {hasAny && (
              <Button variant="ghost" size="sm" className="rounded-full" onClick={() => { setShowForm(false); resetForm(); }} disabled={busy} data-testid="broker-bitunix-cancel-add">Abbrechen</Button>
            )}
            <div className="text-xs text-muted-foreground">Keys werden AES-verschlüsselt (Fernet).</div>
          </div>
        </div>
      )}
      <QRScanDialog open={qrOpen} onOpenChange={setQrOpen}
        onResult={(res) => setForm((f) => ({ ...f, api_key: res.api_key || f.api_key, api_secret: res.api_secret || f.api_secret }))} />
    </div>
  );
}

function LeveXImportCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/broker/levex/status`, { withCredentials: true });
      setStatus(res.data);
    } catch { /* ok */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { toast.error("Bitte zuerst eine CSV-Datei auswählen."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/api/broker/levex/import`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Import abgeschlossen", {
        description: `${res.data.inserted} neu · ${res.data.skipped_existing} bereits vorhanden · ${res.data.invalid_rows} übersprungen`,
      });
      setFile(null);
      await load();
    } catch (e) {
      toast.error("Import fehlgeschlagen", { description: e?.response?.data?.detail || "CSV konnte nicht gelesen werden." });
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/api/broker/levex/disconnect`, { withCredentials: true });
      toast.success("LeveX-Import zurückgesetzt");
      await load();
    } finally { setBusy(false); }
  };

  if (loading) return <div className="card-surface p-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} /></div>;
  const connected = !!status?.connected;

  return (
    <div className="card-surface p-6 max-w-3xl" data-testid="broker-levex-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-secondary border border-border grid place-items-center">
            <FileText className="h-5 w-5 text-primary-foreground/80" strokeWidth={1.5} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-heading text-lg">LeveX (CSV-Import)</h2>
              <Badge variant="secondary" className={`rounded-full text-xs ${connected ? "bg-[hsl(122_39%_49%/0.15)] text-[hsl(122_39%_65%)] border border-[hsl(122_39%_49%/0.35)]" : "bg-secondary border border-border"}`}>
                {connected ? `${status.trade_count} Trades importiert` : "kein Import"}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              LeveX bietet keine öffentliche API. Exportiere deine Trade-Historie im LeveX-Portal als CSV und lade sie hier hoch.
              {status?.last_import_at && ` · letzter Import ${new Date(status.last_import_at).toLocaleString("de-DE")}`}
            </p>
          </div>
        </div>
        {connected && (
          <Button variant="ghost" size="sm" className="text-[hsl(0_66%_72%)]" onClick={disconnect} disabled={busy} data-testid="broker-levex-disconnect">
            <Trash2 className="mr-2 h-4 w-4" strokeWidth={1.5} /> Alle löschen
          </Button>
        )}
      </div>
      <div className="mt-6 space-y-4 border-t border-border pt-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">CSV-Datei (max. 5 MB)</Label>
            <div className="mt-1.5 flex items-center gap-3">
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="broker-levex-file"
                className="text-sm file:mr-3 file:rounded-full file:border-0 file:bg-secondary file:px-4 file:py-2 file:text-sm file:text-foreground hover:file:bg-secondary/80"
              />
              {file && <span className="text-xs text-muted-foreground truncate max-w-[220px]" data-testid="broker-levex-filename">{file.name}</span>}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              Erkannte Spalten: Order-ID, Symbol, Side, Preis, Menge, Fee, PnL, Zeit — Alias-tolerant. Format: UTF-8 oder Latin-1, Trennzeichen wird automatisch erkannt (`,`  `;`  `\t`).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Button className="rounded-full h-11 px-5" onClick={upload} disabled={busy || !file} data-testid="broker-levex-import">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Upload className="mr-2 h-4 w-4" strokeWidth={1.5} />}
            {busy ? "Import läuft…" : "CSV importieren"}
          </Button>
          <div className="text-xs text-muted-foreground">Duplikate werden per Order-/Trade-ID automatisch übersprungen.</div>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, description, checked, onChange, testid, disabled }) {
  return (
    <div className="flex items-start justify-between gap-6 flex-1">
      <div>
        <div className="font-medium">{label}</div>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
    </div>
  );
}
